import { useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';

/** A filter param's value; `null` (like `''`) means "remove the param". */
export type TUrlFilterValue = string | null;

/**
 * A new value for a filter param, or a function deriving it from the current
 * one. Use the function form whenever the next value depends on the previous —
 * a value read during render is stale for a second write in the same frame.
 */
export type TUrlFilterUpdate =
  | TUrlFilterValue
  | ((previous: string | null) => TUrlFilterValue);

/**
 * URL-backed filter state, keyed by a caller-defined set of param names.
 *
 * @typeParam TKey - Union of allowed filter param keys. Pass a string-literal
 * union (e.g. `'q' | 'author' | 'tag'`) so that a mistyped key is a compile
 * error instead of a silent read/write against the wrong param at runtime.
 * Defaults to `string` for callers that don't need the extra safety.
 */
export interface IUrlFilters<TKey extends string = string> {
  /** The current URL query params. */
  searchParams: URLSearchParams;
  /** Read a single filter param, or `null` when it is absent. */
  getFilterParam: (key: TKey) => string | null;
  /**
   * Set a single filter param, or remove it when the value is empty/null. Pass
   * a function to derive the next value from the current one.
   */
  setFilterParam: (key: TKey, value: TUrlFilterUpdate) => void;
  /** Apply related filter changes in one navigation, preserving other params. */
  setFilterParams: (values: Partial<Record<TKey, string | null>>) => void;
  /** Remove several filter params at once (e.g. a "reset filters" action). */
  clearFilterParams: (keys: TKey[]) => void;
}

/**
 * Manage filter state that lives in the URL query string.
 *
 * Keeping filters in the URL makes a filtered view shareable and lets it survive
 * a reload. Updates use `replace` so changing a filter doesn't push a new
 * back/forward history entry, and an empty/`null` value removes the param rather
 * than leaving a `?key=` behind.
 *
 * The optional `TKey` type parameter narrows every key argument to a known set,
 * so callers get autocomplete and typos are caught at compile time:
 *
 * ```ts
 * const { getFilterParam } = useUrlFilters<'q' | 'author' | 'tag'>();
 * getFilterParam('q');       // ok
 * getFilterParam('autor');   // type error
 * ```
 *
 * `setFilterParam` also takes a function, for a value that depends on the one
 * before it — a multi-select toggle, say. Prefer it over recomputing from a
 * value read during render, which is stale for a second write in the same frame:
 *
 * ```ts
 * setFilterParam('tag', prev => toggle(prev, 'finance'));
 * ```
 *
 * Reusable anywhere URL-backed filters/search are needed (library, drafts,
 * integrations, run lists, …).
 *
 * @returns `searchParams` plus `getFilterParam` / `setFilterParam` /
 * `setFilterParams` / `clearFilterParams`.
 */
export function useUrlFilters<
  TKey extends string = string,
>(): IUrlFilters<TKey> {
  const [searchParams, setSearchParams] = useSearchParams();
  // What this frame has already written, and the params it was written against.
  // `setSearchParams` resolves even its own updater form against the params of
  // the render it was created in, so two writes in one frame would both start
  // from the same base and the second would drop the first. Keyed on identity:
  // `searchParams` is memoised on `location.search`, so any re-render with a
  // different URL yields a new object and the pending value is discarded.
  const pendingRef = useRef<{
    base: URLSearchParams;
    next: URLSearchParams;
  } | null>(null);

  const getFilterParam = useCallback(
    (key: TKey) => searchParams.get(key),
    [searchParams],
  );

  const updateParams = useCallback(
    (mutate: (params: URLSearchParams) => void) => {
      const base =
        pendingRef.current?.base === searchParams
          ? pendingRef.current.next
          : searchParams;
      const next = new URLSearchParams(base);
      mutate(next);
      pendingRef.current = { base: searchParams, next };
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  const setFilterParam = useCallback(
    (key: TKey, value: TUrlFilterUpdate) => {
      updateParams(params => {
        const resolved =
          typeof value === 'function' ? value(params.get(key)) : value;
        // Only null/'' remove the param; falsy-but-valid values ('0') are kept.
        if (resolved !== null && resolved !== '') params.set(key, resolved);
        else params.delete(key);
      });
    },
    [updateParams],
  );

  const setFilterParams = useCallback(
    (values: Partial<Record<TKey, string | null>>) => {
      updateParams(params => {
        for (const key of Object.keys(values) as TKey[]) {
          const value = values[key];
          if (value === undefined) continue;
          // Only null/'' remove the param; values such as '0' are kept.
          if (value !== null && value !== '') params.set(key, value);
          else params.delete(key);
        }
      });
    },
    [updateParams],
  );

  const clearFilterParams = useCallback(
    (keys: TKey[]) => {
      updateParams(params => {
        for (const key of keys) params.delete(key);
      });
    },
    [updateParams],
  );

  return {
    searchParams,
    getFilterParam,
    setFilterParam,
    setFilterParams,
    clearFilterParams,
  };
}
